import * as React from 'react';

import { darken, transparentize } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';

export interface TextFieldInlineProps {
    dataId: string;
    label: string;
    id: string;
    name: string;
    autofocus?: boolean;
    defaultValue?: string;
    disabled?: boolean;
    invalid?: boolean;
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    placeholder?: string;
    validationMessage?: string;
    value?: string;
    onChange?: (e: any) => void;
    onBlur?: (e: any) => void;
    maxLength?: number;
    minLength?: number;
    thinLabel?: boolean;
    className?: string;
    labelWidth?: string;
    labelAlignment?: 'start' | 'center' | 'end' | undefined;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
}

interface WrapperProps {
    labelWidth?: string;
}

interface StyledLabelWrapperProps {
    labelAlignment: 'start' | 'center' | 'end';
}

interface StyledValidationMessageProps {
    invalid?: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    display: grid;
    grid-template-columns:
        ${(props: WrapperProps): string => (props.labelWidth ? props.labelWidth : 'auto')}
        minmax(0, 1fr);
`;

const StyledLabelWrapper = styled.div<StyledLabelWrapperProps>`
    display: flex;
    justify-content: center;
    align-items: ${(props: StyledLabelWrapperProps): string => props.labelAlignment};
    flex-direction: column;
    padding-right: ${SPACING.LARGE};
`;

const StyledTextLabel = styled.label<StyledTextLabelProps>`
    margin-right: ${SPACING.X_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.LARGE};
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.div`
    display: inline-flex;
    max-width: 100%;
`;

const StyledInput = styled.input<StyledTextLabelProps>`
    background-color: ${(props: StyledTextLabelProps): string =>
        props.disabled ? darken('0.1', COLOR.PRIMARY.WHITE) : COLOR.PRIMARY.WHITE};
    color: ${(props: StyledTextLabelProps): string =>
        props.disabled ? transparentize('0.5', COLOR.PRIMARY.BLACK) : COLOR.PRIMARY.BLACK};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    padding: ${SPACING.SMALL};
    border-width: 1px;
    border-style: solid;
    border-color: ${(props: StyledTextLabelProps): string =>
        props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD};
    border-radius: ${RADIUS.DEFAULT};

    &::placeholder {
        opacity: 0.65;
    }
`;

const StyledValidationMessage = styled.div<StyledValidationMessageProps>`
    font-size: ${FONT_SIZE.X_SMALL};
    line-height: ${FONT_SIZE.SMALL};
    color: ${(props: StyledValidationMessageProps): string =>
        props.invalid ? COLOR.PRIMARY.RED : COLOR.PRIMARY.BLACK};
    padding-top: ${SPACING.SMALL};
    grid-area: 2 / 2;
`;

export const TextFieldInline = React.forwardRef<HTMLInputElement, TextFieldInlineProps>(
    (
        {
            dataId,
            label,
            id,
            hasHelper,
            helperContent,
            placeholder,
            name,
            autofocus,
            defaultValue,
            disabled,
            invalid,
            onChange,
            onBlur,
            maxLength,
            minLength,
            thinLabel,
            value,
            className,
            validationMessage,
            labelWidth,
            labelAlignment = 'start',
            ...props
        },
        ref,
    ) => {
        return (
            <Wrapper className={className} labelWidth={labelWidth} {...props}>
                <StyledLabelWrapper labelAlignment={labelAlignment}>
                    {hasHelper ? (
                        <StyledLabelHasHelper>
                            <StyledTextLabel invalid={invalid} disabled={disabled} thinLabel={thinLabel} htmlFor={id}>
                                {label}
                            </StyledTextLabel>
                            <HelperModal dataId={dataId} modalContent={helperContent}>
                                <QuestionMark />
                            </HelperModal>
                        </StyledLabelHasHelper>
                    ) : (
                        <StyledTextLabel
                            data-test-id={`${dataId}-label`}
                            invalid={invalid}
                            disabled={disabled}
                            thinLabel={thinLabel}
                            htmlFor={id}
                        >
                            {label}
                        </StyledTextLabel>
                    )}
                </StyledLabelWrapper>
                <StyledInput
                    type="text"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    minLength={minLength}
                    invalid={invalid}
                    disabled={disabled}
                    {...(typeof value !== 'undefined' && { value })}
                    {...(typeof defaultValue !== 'undefined' && { defaultValue })}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                />
                {validationMessage && (
                    <StyledValidationMessage invalid={invalid}>{validationMessage}</StyledValidationMessage>
                )}
            </Wrapper>
        );
    },
);

TextFieldInline.displayName = 'TextFieldInline';
